/**
 * AxLabs Experiments - Simple Harmonic Motion (Spring-Mass System)
 *
 * Demonstrates oscillatory motion with a mass on a spring.
 * Includes damping for realistic behavior.
 *
 * Governing equation: m(d²x/dt²) = -kx - b(dx/dt)
 */
import { BaseSimulationEngine, DIMENSIONS, verletStep, vec2, } from '@axlabs/simulation-core';
// ============================================================================
// PARAMETERS
// ============================================================================
const PARAMETERS = [
    {
        id: 'mass',
        name: 'Mass',
        symbol: 'm',
        unit: 'kg',
        dimension: DIMENSIONS.MASS,
        min: 0.1,
        max: 100,
        default: 1,
        step: 0.1,
        description: 'Mass attached to the spring',
    },
    {
        id: 'springConstant',
        name: 'Spring Constant',
        symbol: 'k',
        unit: 'N/m',
        dimension: { L: 0, M: 1, T: -2, I: 0, Θ: 0, N: 0, J: 0 },
        min: 0.1,
        max: 1000,
        default: 10,
        step: 1,
        description: 'Stiffness of the spring (Hooke\'s law constant)',
    },
    {
        id: 'initialDisplacement',
        name: 'Initial Displacement',
        symbol: 'x_0',
        unit: 'm',
        dimension: DIMENSIONS.LENGTH,
        min: -2,
        max: 2,
        default: 0.5,
        step: 0.05,
        description: 'Starting displacement from equilibrium',
    },
    {
        id: 'initialVelocity',
        name: 'Initial Velocity',
        symbol: 'v_0',
        unit: 'm/s',
        dimension: DIMENSIONS.VELOCITY,
        min: -10,
        max: 10,
        default: 0,
        step: 0.1,
        description: 'Starting velocity',
    },
    {
        id: 'damping',
        name: 'Damping Coefficient',
        symbol: 'b',
        unit: 'kg/s',
        dimension: { L: 0, M: 1, T: -1, I: 0, Θ: 0, N: 0, J: 0 },
        min: 0,
        max: 20,
        default: 0,
        step: 0.1,
        description: 'Viscous damping coefficient (0 = undamped)',
    },
];
// ============================================================================
// EQUATIONS
// ============================================================================
const EQUATIONS = [
    {
        id: 'motion',
        name: 'Equation of Motion',
        latex: 'm\\frac{d^2x}{dt^2} = -kx - b\\frac{dx}{dt}',
        variables: ['mass', 'x', 'springConstant', 'damping', 'v'],
        description: 'Newton\'s second law for damped harmonic oscillator',
    },
    {
        id: 'solution_undamped',
        name: 'Solution (Undamped)',
        latex: 'x(t) = A\\cos(\\omega t + \\phi)',
        variables: ['x'],
        description: 'General solution for undamped oscillator',
        condition: (params) => (params['damping'] ?? 0) === 0,
    },
    {
        id: 'angular_frequency',
        name: 'Angular Frequency',
        latex: '\\omega_0 = \\sqrt{\\frac{k}{m}}',
        variables: ['springConstant', 'mass'],
        description: 'Natural angular frequency of oscillation',
    },
    {
        id: 'period',
        name: 'Period',
        latex: 'T = 2\\pi\\sqrt{\\frac{m}{k}}',
        variables: ['mass', 'springConstant'],
        description: 'Time for one complete oscillation',
    },
    {
        id: 'frequency',
        name: 'Frequency',
        latex: 'f = \\frac{1}{2\\pi}\\sqrt{\\frac{k}{m}}',
        variables: ['mass', 'springConstant'],
        description: 'Number of oscillations per second',
    },
    {
        id: 'damping_ratio',
        name: 'Damping Ratio',
        latex: '\\zeta = \\frac{b}{2\\sqrt{km}}',
        variables: ['damping', 'springConstant', 'mass'],
        description: 'Determines oscillation behavior (ζ < 1: underdamped, ζ = 1: critical, ζ > 1: overdamped)',
        condition: (params) => (params['damping'] ?? 0) > 0,
    },
    {
        id: 'kinetic_energy',
        name: 'Kinetic Energy',
        latex: 'KE = \\frac{1}{2}mv^2',
        variables: ['mass', 'v'],
        description: 'Energy due to motion',
    },
    {
        id: 'potential_energy',
        name: 'Potential Energy',
        latex: 'PE = \\frac{1}{2}kx^2',
        variables: ['springConstant', 'x'],
        description: 'Energy stored in spring deformation',
    },
    {
        id: 'total_energy',
        name: 'Total Energy (Undamped)',
        latex: 'E = \\frac{1}{2}mv^2 + \\frac{1}{2}kx^2 = \\frac{1}{2}kA^2',
        variables: ['mass', 'v', 'springConstant', 'x'],
        description: 'Total mechanical energy (constant without damping)',
        condition: (params) => (params['damping'] ?? 0) === 0,
    },
];
// ============================================================================
// FAILURE MODES
// ============================================================================
const FAILURE_MODES = [
    {
        id: 'spring_exceeds_limit',
        name: 'Spring Limit Exceeded',
        description: 'Displacement exceeds realistic spring limits',
        check: (state) => Math.abs(state.variables['x'] ?? 0) > 5,
        severity: 'warning',
        educationalMessage: 'The spring has been stretched beyond realistic limits. ' +
            'Real springs have elastic limits beyond which they deform permanently.',
    },
    {
        id: 'overdamped',
        name: 'Overdamped System',
        description: 'Damping is so high that no oscillations occur',
        check: (_, params) => {
            const b = params['damping'] ?? 0;
            const k = params['springConstant'] ?? 10;
            const m = params['mass'] ?? 1;
            const zeta = b / (2 * Math.sqrt(k * m));
            return zeta >= 1;
        },
        severity: 'warning',
        educationalMessage: 'The system is critically damped or overdamped (ζ ≥ 1). ' +
            'It will return to equilibrium without oscillating.',
    },
];
// ============================================================================
// EXPERIMENT CONFIG
// ============================================================================
export const SHM_CONFIG = {
    id: 'shm-spring',
    slug: 'shm-spring',
    name: 'Simple Harmonic Motion (Spring-Mass)',
    domain: 'physics',
    description: 'Explore oscillatory motion with a mass on a spring',
    longDescription: `
Simple Harmonic Motion (SHM) is one of the most important types of periodic motion.
A mass attached to a spring experiences a restoring force proportional to displacement.

Key concepts:
- Hooke's Law: F = -kx (restoring force)
- Period depends on mass and spring constant, NOT amplitude
- Energy oscillates between kinetic and potential forms
- Damping causes amplitude decay
- Critical damping: fastest return to equilibrium without oscillation
- Phase space (x vs v) shows elliptical orbits for undamped motion

The damping ratio ζ determines behavior:
- ζ < 1: Underdamped (oscillates with decaying amplitude)
- ζ = 1: Critically damped (fastest non-oscillatory decay)
- ζ > 1: Overdamped (slow exponential decay)
  `.trim(),
    version: 1,
    conceptTags: ['shm', 'oscillation', 'energy-conservation', 'damping', 'phase-space'],
    difficulty: 3,
    prerequisites: ['newtonian-mechanics', 'energy'],
    parameters: PARAMETERS,
    equations: EQUATIONS,
    initialConditions: [
        { variableId: 'x', type: 'parameter', parameterId: 'initialDisplacement' },
        { variableId: 'v', type: 'parameter', parameterId: 'initialVelocity' },
    ],
    failureModes: FAILURE_MODES,
    recommendedDt: 1 / 120,
    maxDt: 1 / 30,
};
// ============================================================================
// SIMULATION ENGINE
// ============================================================================
export class SHMSimulation extends BaseSimulationEngine {
    config = SHM_CONFIG;
    constructor() {
        super({ x: 0, v: 0, a: 0 }, { type: 'verlet', dt: 1 / 120, maxSubsteps: 10 });
    }
    getInitialState(params) {
        const x0 = params['initialDisplacement'] ?? 0.5;
        const v0 = params['initialVelocity'] ?? 0;
        const k = params['springConstant'] ?? 10;
        const m = params['mass'] ?? 1;
        const b = params['damping'] ?? 0;
        // Initial acceleration
        const a0 = (-k * x0 - b * v0) / m;
        return {
            x: x0,
            v: v0,
            a: a0,
        };
    }
    computeDerivatives(state, params) {
        const m = params['mass'] ?? 1;
        const k = params['springConstant'] ?? 10;
        const b = params['damping'] ?? 0;
        // Acceleration: a = (-kx - bv) / m
        const a = (-k * state.x - b * state.v) / m;
        return {
            x: state.v,
            v: a,
        };
    }
    integrate(dt) {
        const m = this.parameters['mass'] ?? 1;
        const k = this.parameters['springConstant'] ?? 10;
        const b = this.parameters['damping'] ?? 0;
        // Use Verlet for better energy conservation
        const getAcceleration = (pos, vel) => {
            const x = pos.x;
            const v = vel.x;
            const a = (-k * x - b * v) / m;
            return vec2(a, 0);
        };
        const currentPos = vec2(this.state.x, 0);
        const currentVel = vec2(this.state.v, 0);
        const currentAcc = getAcceleration(currentPos, currentVel);
        const result = verletStep(currentPos, currentVel, currentAcc, getAcceleration, dt);
        this.state.x = result.position.x;
        this.state.v = result.velocity.x;
        this.state.a = getAcceleration(result.position, result.velocity).x;
    }
    computeEnergy(state, params) {
        const m = params['mass'] ?? 1;
        const k = params['springConstant'] ?? 10;
        const kinetic = 0.5 * m * state.v * state.v;
        const potential = 0.5 * k * state.x * state.x;
        return {
            kinetic,
            potential,
            thermal: 0,
            total: kinetic + potential,
        };
    }
    getPositionForStabilityCheck() {
        return this.state.x;
    }
    getBounds() {
        return {
            x: { min: -10, max: 10 },
        };
    }
    /**
     * Get angular frequency ω₀
     */
    getAngularFrequency() {
        const k = this.parameters['springConstant'] ?? 10;
        const m = this.parameters['mass'] ?? 1;
        return Math.sqrt(k / m);
    }
    /**
     * Get period T
     */
    getPeriod() {
        return (2 * Math.PI) / this.getAngularFrequency();
    }
    /**
     * Get frequency f
     */
    getFrequency() {
        return this.getAngularFrequency() / (2 * Math.PI);
    }
    /**
     * Get damping ratio ζ
     */
    getDampingRatio() {
        const b = this.parameters['damping'] ?? 0;
        const k = this.parameters['springConstant'] ?? 10;
        const m = this.parameters['mass'] ?? 1;
        return b / (2 * Math.sqrt(k * m));
    }
    /**
     * Get damped angular frequency ωd (for underdamped systems)
     */
    getDampedAngularFrequency() {
        const zeta = this.getDampingRatio();
        if (zeta >= 1) {
            return null; // No oscillation
        }
        const omega0 = this.getAngularFrequency();
        return omega0 * Math.sqrt(1 - zeta * zeta);
    }
    /**
     * Get amplitude of oscillation
     */
    getAmplitude() {
        const k = this.parameters['springConstant'] ?? 10;
        // E = (1/2)kA² => A = sqrt(2E/k)
        const energy = this.computeEnergy(this.state, this.parameters);
        return Math.sqrt(2 * energy.total / k);
    }
}
/**
 * Factory function to create a new SHM simulation
 */
export function createSHM() {
    return new SHMSimulation();
}
//# sourceMappingURL=shm-spring.js.map